import { groupBy, nonNullable } from 'lib/array/arrayUtils'
import { createSelector } from 'reselect'
import offerPageURL from 'lib/offer/offerPageURL'
import { getLabels } from 'checkout/lib/utils/accommodation/label'
import { getLocationString } from 'checkout/lib/utils/accommodation/location'
import { getCurrentIntl } from 'i18n/util/currentIntl'
import { excludeNullOrUndefined, getAccommodationDesignationWithTourBundle, getAccommodationDesignationWithCruise } from 'checkout/utils'
import { getBedbankRateKey } from 'lib/hotels/getBedbankRateKey'
import { getBedbankItemView } from 'checkout/lib/utils/bedbank/view'
import { getFullOffers } from 'selectors/offerSelectors'
import { getIsLuxPlusLPPEnabled, getLppMemberPricingEnabledOnOrder } from 'luxPlus/selectors/featureToggle'
import getBedbankItems from 'checkout/selectors/view/items/getBedbankItems'
import { isBedbankOffer } from 'lib/offer/offerTypes'
import getFlightBundledItemIds from '../getFlightBundledItemIds'
import { diffInDays } from 'lib/datetime/dateUtils'
import { getLoyaltyBedbankPropertyStateProvince } from 'loyalty/lib/pointsCalculation/earnKey/getLoyaltyEarnKey'
import { findPostPurchaseCheckout } from 'lib/checkout/checkoutUtils'
import { getTourV2Items } from 'checkout/selectors/view/toursv2'
import { getCruiseItems } from 'checkout/selectors/cruiseSelectors'
import { getChannelMarkup } from 'selectors/channelMarkupSelector'

const getBedbankItemViews = createSelector(
  (state: App.State) => getBedbankItems(state),
  (state: App.State) => getFullOffers(state),
  (state: App.State) => state.offer.bedbankOfferRates,
  (state: App.State) => state.offer.propertyRates,
  (state: App.State) => findPostPurchaseCheckout(state.checkout.cart.mode),
  (state: App.State) => state.checkout.cart.existingOrder,
  (state: App.State) => state.checkout.sessions,
  (state: App.State) => getFlightBundledItemIds(state),
  // This is temporary until LPP member pricing is fully released
  (state: App.State) => getIsLuxPlusLPPEnabled(state),
  (state: App.State) => getTourV2Items(state),
  (state: App.State) => getCruiseItems(state),
  // Match the backend member-pricing condition for post-purchase previews.
  (state: App.State) => getLppMemberPricingEnabledOnOrder(state),
  (state: App.State) => getChannelMarkup(state),
  (
    cartItems,
    offers,
    bedbankRatesByOffer,
    propertyRatesByKey,
    postPurchase,
    existingOrder,
    sessions,
    bundledItemIds,
    luxPlusLPPEnabled,
    tourV2Items,
    cruiseItems,
    lppMemberPricingEnabledOnOrder,
    channelMarkup,
  ): App.WithDataStatus<Array<App.Checkout.BedbankAccommodationOfferView>> => {
    // Each item = 1 room, but we want to process the same dates/offer as a single view
    const itemsByBookingKey = groupBy(cartItems, item => `${item.offerId}-${item.checkIn}-${item.checkOut}`)
    const itemGroups = itemsByBookingKey.values().map(items => ({
      checkIn: items[0].checkIn,
      checkOut: items[0].checkOut,
      offerId: items[0].offerId,
      items,
    })).toArray()

    const views = itemGroups.map((itemGroup): App.WithDataStatus<App.Checkout.BedbankAccommodationOfferView | undefined> => {
      const { offerId, checkIn, checkOut, items } = itemGroup
      const offer = offers[offerId]

      if (!isBedbankOffer(offer)) {
        return {
          hasRequiredData: false,
          data: undefined,
        }
      }

      const itemsBySessionId = groupBy(items, (item) => item.sessionId ?? 'none')

      let bedbankOfferRate: App.BedbankRate | undefined
      const itemViewsWithStatusesAndBedGroups = items.map(item => {
        const groupData = itemsBySessionId.get(item.sessionId ?? 'none') ?? []
        const combinedOccupancy = groupData.length > 0 ? groupData.map(item => item.occupancy) : [item.occupancy]
        const combinedRateKey = getBedbankRateKey(combinedOccupancy, checkIn, checkOut)
        const perRoomRateKey = getBedbankRateKey([item.occupancy], checkIn, checkOut)
        const bedbankOfferRates = bedbankRatesByOffer[offerId]?.[combinedRateKey]
          ?? bedbankRatesByOffer[offerId]?.[perRoomRateKey]
        const bedbankRatesFetched = !!bedbankRatesByOffer[offerId]
        bedbankOfferRate = bedbankOfferRates?.find(rate => rate.id === item.roomRateId && rate.isFlightBundle === item.isFlightBundle && rate.roomId === item.roomId)

        return {
          bedGroups: bedbankOfferRate?.bedGroups ?? [],
          itemViewsWithStatuses: getBedbankItemView(
            getCurrentIntl(),
            item,
            luxPlusLPPEnabled,
            sessions[item.sessionId ?? 'none']?.data ?? null,
            offer,
            bedbankOfferRate,
            propertyRatesByKey,
            postPurchase,
            existingOrder,
            bedbankRatesFetched,
            lppMemberPricingEnabledOnOrder,
            channelMarkup,
          ),
        }
      })

      const duration = diffInDays(new Date(checkIn), new Date(checkOut))

      const bundledWithTour = items.some(item => !!item.bundledItemIds?.length)
      const itemBundleId = items[0]?.bundledItemIds?.[0]
      const designation = bundledWithTour
        ? getAccommodationDesignationWithTourBundle(checkIn, itemBundleId, tourV2Items)
        : getAccommodationDesignationWithCruise(checkIn, checkOut, cruiseItems)

      const view: App.Checkout.BedbankAccommodationOfferView = {
        offerId,
        offer,
        offerPageUrl: offerPageURL(offer),
        ...getLabels(offer),
        bedGroups: itemViewsWithStatusesAndBedGroups.flatMap(i => i.bedGroups),
        confidenceLabels: [],
        duration,
        startDate: checkIn,
        endDate: checkOut,
        image: offer?.image,
        location: getLocationString(getCurrentIntl(), offer),
        locationCountry: offer.property.address.countryName,
        locationCountryCode: offer.property.address.countryCode,
        locationStateProvince: getLoyaltyBedbankPropertyStateProvince(offer.property.address),
        occupancy: items.map(item => item.occupancy).filter(excludeNullOrUndefined),
        offerType: offer?.type,
        reservationType: 'instant_booking',
        urgencyLabels: [],
        saleUnit: offer?.saleUnitLong,
        durationLabel: getCurrentIntl().formatMessage({ id: 'checkout.bedbank.nightCount', defaultMessage: '{count, plural, one {# night} other {# nights}}', description: 'Night count in bedbank accommodation duration label' }, { count: duration }),
        offerLoaded: true,
        itemViews: itemViewsWithStatusesAndBedGroups.map(i => i.itemViewsWithStatuses.data),
        propertyTimezone: offer.property.timezone,
        bundledWithFlightsOnly: false,
        bundledWithTour,
        designation,
        isBundled: items.some(item => bundledItemIds.has(item.itemId)),
        hasMobilePromotion: !!bedbankOfferRate?.mobilePromotion,
        propertyId: offer.property.id,
      }

      return {
        data: view,
        hasRequiredData: itemViewsWithStatusesAndBedGroups.every(itemView => itemView.itemViewsWithStatuses.hasRequiredData),
      }
    })

    return {
      data: nonNullable(views.map(view => view.data)),
      hasRequiredData: views.every(view => view.hasRequiredData),
    }
  },
)

export default getBedbankItemViews
